import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { costService } from '../Services/CostService';
import { CostBreakdownRow } from '../Model/CostType';
import {
  fmtFull,
  leafType,
  NOK,
  CurrencyConfig,
} from '../../../Components/Layout/SqlServer/Common/util';

interface OrphansTabProps {
  currency?: CurrencyConfig;
  baseCurrency?: string;
  filter: { from: string; to: string };
}

const OrphansTab: React.FC<OrphansTabProps> = ({
  currency = NOK,
  baseCurrency = 'NOK',
  filter,
}) => {
  const [orphans, setOrphans] = useState<CostBreakdownRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        // Fetch resources attributed to the "(Unknown)" virtual tenant
        const data = await costService.getTenantBreakdown(
          '(Unknown)',
          filter.from,
          filter.to,
        );
        if (!cancelled) setOrphans(data);
      } catch (err) {
        console.error('Failed to load orphans', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [filter.from, filter.to]);

  const total = orphans.reduce((s, o) => s + (o.Cost || 0), 0);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Alert
          severity="warning"
          icon={<WarningAmberIcon fontSize="small" />}
          sx={{
            flex: 1,
            bgcolor: 'rgba(245,158,11,0.1)',
            border: '1px solid rgba(245,158,11,0.3)',
            color: 'warning.main',
            '& .MuiAlert-icon': { color: 'warning.main' },
          }}
        >
          These resources have <strong>ReviewRequired=Yes</strong> —
          unclassified by TagRouter. They carry real cost but cannot be
          attributed to any tenant.
        </Alert>
        <Button
          variant="contained"
          color="warning"
          size="small"
          startIcon={<FileDownloadIcon />}
          sx={{ ml: 2, height: 32, fontWeight: 700 }}
          onClick={() => {
            const header =
              'Name,ResourceGroup,Type,Cost,ReviewReason,ResourceId\n';
            const rows = orphans
              .map(
                (o) =>
                  `"${o.ResourceName}","${o.ResourceGroup}","${o.ResourceType}",${o.Cost},"${o.ReviewReason}","${o.ResourceId}"`,
              )
              .join('\n');
            const blob = new Blob([header + rows], { type: 'text/csv' });
            const url = globalThisThis.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Orphans_${new Date().toISOString().slice(0, 10)}.csv`;
            a.click();
          }}
        >
          Export Orphans
        </Button>
      </Box>

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{ border: '1px solid', borderColor: 'divider' }}
      >
        <Table size="small">
          <TableHead sx={{ bgcolor: 'action.hover' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Resource Name</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Resource Group</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Review Reason</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Resource Type</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>
                Cost
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {orphans.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No unclassified resources found for this period.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              orphans.map((o) => (
                <TableRow key={o.ResourceId.ResourceId} hover>
                  <TableCell>
                    <Tooltip title="View in Azure Portal">
                      <Typography
                        component="a"
                        href={`https://portal.azure.com/#resource${o.ResourceId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          fontSize: 12,
                          fontFamily: 'monospace',
                          color: 'primary.main',
                          textDecoration: 'none',
                          '&:hover': { textDecoration: 'underline' },
                          fontWeight: 600,
                        }}
                      >
                        {o.ResourceName}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                      {o.ResourceGroup}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: 'warning.main',
                        fontWeight: 500,
                      }}
                    >
                      {o.ReviewReason || 'Unclassified'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                      {leafType(o.ResourceType)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography
                      sx={{
                        fontSize: 12,
                        fontFamily: 'monospace',
                        fontWeight: 700,
                        color: 'error.main',
                      }}
                    >
                      {fmtFull(o.Cost, currency, baseCurrency)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 1 }}>
        <Typography
          sx={{
            fontSize: 14,
            fontFamily: 'monospace',
            fontWeight: 800,
            color: 'text.primary',
          }}
        >
          Total unclassified cost: {fmtFull(total, currency, baseCurrency)}
        </Typography>
      </Box>
    </Box>
  );
};

export default OrphansTab;
